import { useTranslation } from "@ncino/pax-ui";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { PageHeader } from "@/shared/components/PageHeader";
import { CheckCircleIcon } from "@phosphor-icons/react";
import { PageContentContainer } from "@/shared/components/PageContentContainer";
import PaxListElement from "@/shared/components/elements/PaxListElement";
import { toTableElementConfig } from "@/routes/manage/-internal/ValidationStep/boaTable";
import { TasksHeader } from "./-internal/tasksHeader";
import { TaskTab, makeTaskColumns } from "./-internal/types";
import { useNotifications } from "./-internal/useNotifications";

export const Route = createFileRoute("/tasks/")({
  staticData: {
    breadcrumb: { title: ({ t }) => t("breadcrumbs.tasks"), icon: CheckCircleIcon },
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { t, i18n } = useTranslation("tasks");
  const locale = i18n.language.replace("_", "-");

  const { institutionId, userName } = Route.useRouteContext();
  const clientId = institutionId;
  const currentUser = userName;

  const columns = useMemo(() => makeTaskColumns(t, locale), [t, locale]);

  const { notifications, isLoading, isError, isUpdating, setAcknowledged } = useNotifications(
    clientId,
    currentUser
  );

  const [tab, setTab] = useState<TaskTab>("incomplete");
  // Selection is keyed by id, not row index, so it survives refetches.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const { incomplete, complete } = useMemo(
    () => ({
      incomplete: notifications.filter((n: any) => !n.isAcknowledged),
      complete: notifications.filter((n: any) => n.isAcknowledged),
    }),
    [notifications]
  );

  const tabRows = tab === "incomplete" ? incomplete : complete;

  // Only count ids still visible on this tab (rows can move tabs after a refetch).
  const visibleSelectedIds = tabRows
    .filter((r: any) => selectedIds.has(r.id))
    .map((r: any) => r.id);
  // PaxListElement selection is positional; safe because sorting is disabled below,
  // so displayed order === tabRows order.
  const selectedRows = tabRows.map((r: any) => selectedIds.has(r.id));

  const elementData = useMemo(() => {
    const data = toTableElementConfig(columns, tabRows);
    // PROCESSING makes PaxListElement render its skeleton rows.
    return isLoading ? { ...data, status: "PROCESSING" as const } : data;
  }, [columns, tabRows, isLoading]);

  const clearSelection = () => setSelectedIds(new Set());

  const handleTabChange = (next: TaskTab) => {
    setTab(next);
    clearSelection();
  };

  const toggleRow = (rowIdx: number) => {
    const id = tabRows[rowIdx]?.id;
    if (id == null) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    const allSelected = tabRows.length > 0 && visibleSelectedIds.length === tabRows.length;
    setSelectedIds(allSelected ? new Set() : new Set(tabRows.map((r: any) => r.id)));
  };

  const handleBulkAction = async () => {
    // Incomplete tab → mark complete (true); Completed tab → mark incomplete (false).
    try {
      await setAcknowledged(visibleSelectedIds, tab === "incomplete");
      clearSelection();
    } catch (err) {
      // Keep the selection so the user can retry. TODO: surface via your snackbar/toast.
      console.error("Failed to update tasks", err);
    }
  };

  return (
    <>
      <PageHeader title={t("tasksPage.title")} />
      <PageContentContainer>
        <PaxListElement
          elementData={elementData}
          title=""
          showHeader
          customHeader={
            <TasksHeader
              tab={tab}
              onTabChange={handleTabChange}
              incompleteCount={incomplete.length}
              completeCount={complete.length}
              selectedCount={visibleSelectedIds.length}
              onBulkAction={handleBulkAction}
              onClearSelection={clearSelection}
              isUpdating={isUpdating}
            />
          }
          elevated={false}
          isElementTable
          tableHeaderVariant="dark"
          showIndex={false}
          disableSort
          selectable={tabRows.length > 0}
          selectedRows={selectedRows}
          onSelect={toggleRow}
          onSelectAll={toggleAll}
          isEmpty={!isLoading && !isError && tabRows.length === 0}
          emptyStateSize="compact"
          emptyStateTitle={
            tab === "incomplete" ? t("tasksPage.empty.incomplete") : t("tasksPage.empty.complete")
          }
        />
      </PageContentContainer>
    </>
  );
}
